import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';

import { FishGroupsController_getDetail } from '@/services/services';
import type { FishGroupDetailDto } from '@/services/types';

export interface FishGroupPopulationInfo {
    fishGroupId: number;
    /** Initial population recorded at creation; null if not tracked. */
    initialPopulationSize: number | null;
    /** True while the underlying detail query is in flight. */
    isLoading: boolean;
}

export interface FishGroupPopulationContext {
    /** Lookup map keyed by fishGroupId. Always contains an entry for every requested id. */
    byId: Map<number, FishGroupPopulationInfo>;
    /** True while any of the requested ids is still resolving. */
    isLoading: boolean;
}

const STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Fetch population information for a list of fish groups in one go. Shares
 * the cache with `useGetFishGroupDetail` (same query key as the generated
 * SDK hook) so this is essentially free when the operator has already
 * navigated to those FGs in the detail modal.
 *
 * Why initial-population only (and not "current"): the current population
 * would require deriving from events (mortality / harvest / measurement).
 * That's a follow-up; in the meantime the initial value is a useful
 * reference for allocation decisions and is shown in the UI labelled
 * accordingly so the operator doesn't mistake it for the live count.
 */
export const useFishGroupPopulationContext = (
    clientId: number,
    fishGroupIds: readonly number[]
): FishGroupPopulationContext => {
    // Deduplicate so we don't fire the same query twice; preserve order
    // for stable hook execution between renders.
    const uniqueIds = useMemo(() => Array.from(new Set(fishGroupIds)), [fishGroupIds]);

    const results = useQueries({
        queries: uniqueIds.map((id) => ({
            queryKey: [FishGroupsController_getDetail.key, clientId, id],
            queryFn: () => FishGroupsController_getDetail(clientId, id),
            staleTime: STALE_TIME_MS,
        })),
    });

    return useMemo<FishGroupPopulationContext>(() => {
        const byId = new Map<number, FishGroupPopulationInfo>();
        let anyLoading = false;
        uniqueIds.forEach((id, index) => {
            const query = results[index];
            const detail = query?.data?.data as FishGroupDetailDto | undefined;
            byId.set(id, {
                fishGroupId: id,
                initialPopulationSize: detail?.initialPopulationSize ?? null,
                isLoading: query?.isPending ?? false,
            });
            if (query?.isPending) anyLoading = true;
        });
        return { byId, isLoading: anyLoading };
    }, [results, uniqueIds]);
};
